import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';

/**
 * Classe base para todos os serviços CRUD
 * Fornece métodos padrão: list, get, create, update, delete
 */
export class BaseService {
    constructor(endpoint) {
        if (!endpoint) {
            throw new Error('BaseService requires an endpoint');
        }
        this.endpoint = endpoint;
    }

    /**
     * Lista registros com paginação, ordenação e filtros
     * @param {Object} params - Parâmetros da listagem
     * @param {number} params.page - Número da página (0-indexed)
     * @param {number} params.size - Tamanho da página
     * @param {string} params.sort - Campo e direção de ordenação (ex: "field,asc")
     * @param {Object} params.filters - Objeto com filtros opcionais
     * @returns {Promise<Object>} Resposta da API com content e paginação
     */
    async list({ page = 0, size = 5, sort, filters = {}, ...otherParams } = {}) {
        // Se filters foi passado explicitamente como objeto (não vazio), usa ele
        // Caso contrário, usa otherParams (filtros passados diretamente) como filtros
        const hasExplicitFilters = filters && typeof filters === 'object' && !Array.isArray(filters) && Object.keys(filters).length > 0;
        const finalFilters = hasExplicitFilters ? filters : otherParams;
        const queryString = buildQueryString({ page, size, sort, filters: finalFilters });
        const response = await httpClient.get(`${this.endpoint}?${queryString}`);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response.json();
    }

    /**
     * Busca um registro por ID
     * @param {string|number} id - ID do registro
     * @param {Object} queryParams - Parâmetros adicionais para a query string
     * @returns {Promise<Object>} Dados do registro
     */
    async get(id, queryParams = {}) {
        const params = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });
        
        const queryString = params.toString();
        const url = queryString 
            ? `${this.endpoint}/${id}?${queryString}`
            : `${this.endpoint}/${id}`;
            
        const response = await httpClient.get(url);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response.json();
    }

    /**
     * Cria um novo registro
     * @param {Object} data - Dados do registro
     * @returns {Promise<Response>} Resposta da API
     */
    async create(data) {
        const response = await httpClient.post(this.endpoint, data);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response;
    }

    /**
     * Atualiza um registro existente
     * @param {string|number} id - ID do registro
     * @param {Object} data - Dados atualizados
     * @returns {Promise<Response>} Resposta da API
     */
    async update(id, data) {
        const response = await httpClient.put(`${this.endpoint}/${id}`, data);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response;
    }

    /**
     * Atualiza um registro existente (método alternativo sem ID na URL)
     * @param {Object} data - Dados atualizados (deve conter o ID)
     * @returns {Promise<Response>} Resposta da API
     */
    async updateWithoutId(data) {
        const response = await httpClient.put(this.endpoint, data);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response;
    }

    /**
     * Deleta um registro
     * @param {string|number} id - ID do registro
     * @param {Object} queryParams - Parâmetros adicionais para a query string
     * @returns {Promise<Response>} Resposta da API
     */
    async delete(id, queryParams = {}) {
        const params = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });
        
        const queryString = params.toString();
        const url = queryString 
            ? `${this.endpoint}/${id}?${queryString}`
            : `${this.endpoint}/${id}`;
            
        const response = await httpClient.delete(url);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response;
    }

    /**
     * Patch genérico para operações específicas
     * @param {string|number} id - ID do registro
     * @param {string} action - Ação específica (ex: "status")
     * @param {Object} data - Dados opcionais
     * @returns {Promise<Response>} Resposta da API
     */
    async patch(id, action, data = null) {
        const url = action 
            ? `${this.endpoint}/${id}/${action}`
            : `${this.endpoint}/${id}`;
            
        const response = await httpClient.patch(url, data);
        
        if (!response.ok) {
            throw await parseApiError(response);
        }
        
        return response;
    }
}
